import React, { useEffect, useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Phone, ExternalLink, CheckCircle, AlertCircle } from "lucide-react";
import { useOnboardingState } from "@/integrations/supabase/onboarding";

const DNITestPage = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [format, setFormat] = useState<'e164' | 'national' | 'international'>(() => {
    const params = new URLSearchParams(window.location.search);
    const f = params.get('format') as any;
    return f === 'national' || f === 'international' ? f : 'e164';
  });
  const { onboarding } = useOnboardingState();
  const params = new URLSearchParams(window.location.search);
  const tenantFromQuery = params.get('tenant_id') || "";
  const tenantId = tenantFromQuery || onboarding?.tenant_id || "";

  const buildSrc = () => {
    const base = "https://ktywphxmqafinzfgzhps.supabase.co/functions/v1/Calie_dniScript";
    const sp = new URLSearchParams();
    if (tenantId) sp.set('tenant_id', tenantId);
    if (format) sp.set('format', format);
    return `${base}?${sp.toString()}`;
  };

  const [scriptSrc, setScriptSrc] = useState(buildSrc());

  useEffect(() => {
    document.title = "DNI Test | Calie.ai";
  }, []);

  useEffect(() => {
    const src = buildSrc();
    setScriptSrc(src);
    setScriptLoaded(false);
    const script = document.createElement('script');
    script.defer = true;
    script.src = src;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setScriptLoaded(false);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [tenantId, format]);

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8 space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold">DNI Test Page</h1>
          <p className="text-muted-foreground">
            This page tests Dynamic Number Insertion (DNI) functionality
          </p>
          <div className="flex flex-col items-center justify-center gap-3">
            {scriptLoaded ? (
              <Badge variant="default" className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                DNI Script Loaded
              </Badge>
            ) : (
              <Badge variant="secondary" className="flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                DNI Script Loading...
              </Badge>
            )}
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Format</span>
              <Select value={format} onValueChange={(v) => setFormat(v as any)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="e164">E.164 (+15551234567)</SelectItem>
                  <SelectItem value="national">National ((555) 123-4567)</SelectItem>
                  <SelectItem value="international">International (+1 555-123-4567)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tenantId ? (
              <Badge variant="outline">Tenant: {tenantId}</Badge>
            ) : (
              <Badge variant="secondary">No tenant_id detected</Badge>
            )}
          </div>
        </div>

        <div className="grid gap-6">
          {/* Basic Phone Number Replacement */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Basic Phone Number Replacement
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">Static Phone Number (should be replaced):</h3>
                <p className="text-lg">
                  Call us at <span data-dni="phone" className="font-bold text-primary">555-123-4567</span>
                </p>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">Multiple Phone Numbers:</h3>
                <p>
                  Main office: <span data-dni="phone" className="font-bold text-primary">555-123-4567</span>
                </p>
                <p>
                  Support line: <span data-dni="phone" className="font-bold text-primary">555-987-6543</span>
                </p>
              </div>
            </div>
          </Card>

          {/* Click-to-Call Links */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Click-to-Call Links
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">Phone Link (href should be updated):</h3>
                <a 
                  data-dni-href="tel" 
                  href="tel:555-123-4567"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <Phone className="h-4 w-4" />
                  <span data-dni="phone">555-123-4567</span>
                </a>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">Button with Phone Number:</h3>
                <button className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90">
                  Call <span data-dni="phone">555-123-4567</span> Now
                </button>
              </div>
            </div>
          </Card>

          {/* Different HTML Structures */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Different HTML Structures</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">In a Div:</h3>
                <div className="text-center p-4 border-2 border-dashed border-border rounded">
                  <div data-dni="phone" className="text-2xl font-bold text-primary">555-123-4567</div>
                </div>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">In a Paragraph:</h3>
                <p className="text-center p-4 border-2 border-dashed border-border rounded">
                  Contact: <span data-dni="phone" className="font-mono bg-background px-2 py-1 rounded">555-123-4567</span>
                </p>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">In a List:</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Sales: <span data-dni="phone">555-123-4567</span></li>
                  <li>Support: <span data-dni="phone">555-987-6543</span></li>
                </ul>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">In a Header:</h3>
                <h4 className="text-lg text-center p-4 border-2 border-dashed border-border rounded">
                  Call <span data-dni="phone" className="text-primary">555-123-4567</span>
                </h4>
              </div>
            </div>
          </Card>

          {/* What to Expect */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">What to Expect</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <p>All phone numbers marked with <code className="bg-muted px-1 rounded">data-dni="phone"</code> should be replaced with your tracking number</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <p>Links with <code className="bg-muted px-1 rounded">data-dni-href="tel"</code> should have their href updated to use the tracking number</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <p>If no tracking number is configured, you'll see the original numbers (555-123-4567, etc.)</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <p>Once your onboarding is complete and a number is assigned, refresh this page to see the replacement</p>
              </div>
            </div>
          </Card>

          {/* Debug Info */}
          <Card className="p-6 bg-muted/50">
            <h2 className="text-xl font-semibold mb-4">Debug Information</h2>
            <div className="text-sm space-y-2 font-mono">
              <p><strong>Script URL:</strong> {scriptSrc}</p>
              <p><strong>Script Status:</strong> {scriptLoaded ? 'Loaded' : 'Loading...'}</p>
              <p><strong>Elements with data-dni="phone":</strong> {document.querySelectorAll('[data-dni="phone"]').length}</p>
              <p><strong>Elements with data-dni-href="tel":</strong> {document.querySelectorAll('[data-dni-href="tel"]').length}</p>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default DNITestPage;